import { z } from 'zod';

import { jobClusterWithTypeSchemaTransformed } from '~/shared/data-contract/job-cluster';
import { jobFamilyWithTypeSchemaTransformed } from '~/shared/data-contract/job-family';
import type { LanguageCodeForJSA } from '~/shared/internationalization';

/** Translation entry for a single language on a JSA preview item (cluster or family). */
export type JsaPreviewItemTranslation = {
    readonly description: string;
    readonly language: LanguageCodeForJSA;
    readonly title: string;
};

export type JsaPreviewItemInput = {
    readonly id: string;
    readonly children: readonly JsaPreviewItemInput[];
    readonly level: 'cluster' | 'family';
    readonly translations: readonly JsaPreviewItemTranslation[];
};

export type RegenerateOnboardingJsaDraft = readonly JsaPreviewItemInput[];

export const jsaStructureRegenerationResponseSchema = z.object({
    streamPath: z.string(),
});

export type JsaStructureRegenerationResponse = z.infer<typeof jsaStructureRegenerationResponseSchema>;

const regenerationStatusSchema = z
    .enum(['ADDED', 'REMOVED'])
    .nullable()
    .transform((value): 'added' | 'confirmed' | 'removed' => {
        if (value === 'ADDED') return 'added';
        if (value === 'REMOVED') return 'removed';

        return 'confirmed';
    });

const regeneratedClusterSchema = jobClusterWithTypeSchemaTransformed.and(
    z.object({ status: regenerationStatusSchema }),
);

const regeneratedFamilySchema = jobFamilyWithTypeSchemaTransformed.and(
    z.object({
        clusters: z.array(regeneratedClusterSchema),
        status: regenerationStatusSchema,
    }),
);

export const jsaStructurePreviewSchema = z.array(regeneratedFamilySchema);

export type JsaStructurePreview = z.infer<typeof jsaStructurePreviewSchema>;
export type JsaStructurePreviewInput = z.input<typeof jsaStructurePreviewSchema>;

export const jsaStructureRegenerationEventSchema = z.object({
    payloadV2: z.object({
        regenerationId: z.string(),
        status: z.enum(['COMPLETED', 'FAILED']),
    }),
});

type BackendStructureNode = {
    readonly id: string;
    readonly afterId: string | null;
    readonly beforeId: string | null;
    readonly childCount: number;
    readonly children: readonly BackendStructureNode[];
    readonly description?: string;
    readonly level: string;
    readonly orderIndex: number;
    readonly parentId: string | null;
    readonly parentLevel: string;
    readonly previewState: string;
    readonly title: string;
    readonly translations: ReadonlyArray<{
        readonly description?: string;
        readonly language: string;
        readonly title: string;
    }>;
};

const backendStructureNodeSchema: z.ZodType<BackendStructureNode> = z.lazy(() =>
    z.object({
        id: z.string(),
        afterId: z.string().nullable().default(null),
        beforeId: z.string().nullable().default(null),
        childCount: z.number().int().nonnegative().default(0),
        children: z.array(backendStructureNodeSchema),
        description: z.string().optional(),
        level: z.string(),
        orderIndex: z.number().int().default(0),
        parentId: z.string().nullable(),
        parentLevel: z.string(),
        previewState: z.string().default('unmodified'),
        title: z.string(),
        translations: z.array(
            z.object({
                description: z.string().optional(),
                language: z.string(),
                title: z.string(),
            }),
        ),
    }),
);

export const jsaStructureRegenerationContentSchema = z.array(backendStructureNodeSchema);

export type JsaStructureRegenerationContent = z.infer<typeof jsaStructureRegenerationContentSchema>;

export type RemovedStructureNode = {
    readonly id: string;
    readonly level: string;
    readonly parentId: string;
    readonly parentLevel: string;
    readonly removedChildrenIds: readonly RemovedStructureNode[];
};

export const removedStructureNodeSchema: z.ZodType<RemovedStructureNode> = z.lazy(() =>
    z.object({
        id: z.string(),
        level: z.string(),
        parentId: z.string(),
        parentLevel: z.string(),
        removedChildrenIds: z.array(removedStructureNodeSchema),
    }),
);

export const removedStructureContentSchema = z.array(removedStructureNodeSchema);

export type RemovedStructureContent = z.infer<typeof removedStructureContentSchema>;

const mapPreviewStateToStatus = (previewState: string): 'added' | 'confirmed' | 'removed' => {
    if (previewState === 'added') return 'added';
    if (previewState === 'removed') return 'removed';

    return 'confirmed';
};

const defaultStatistics: JsaStructurePreview[number]['statistics'] = {
    approvedLevels: 0,
    approvedRoles: 0,
    clustersCount: 0,
    expected: 0,
    familiesCount: 0,
    levelsCount: 0,
    profiles: 0,
    publishedLevels: 0,
    publishedRoles: 0,
    reviewed: 0,
    reviewerLevels: 0,
    reviewerRoles: 0,
    reviewers: 0,
    rolesCount: 0,
};

const defaultActions: JsaStructurePreview[number]['actions'] = {
    canEdit: false,
    canReadDebugInfo: false,
};

export const structureRegenerationContentToPreview = (
    content: JsaStructureRegenerationContent,
    architectureId: string,
): JsaStructurePreview =>
    content.map((node) => ({
        id: node.id,
        actions: defaultActions,
        architectureId,
        childCount: node.childCount,
        clusters: node.children.map((c: BackendStructureNode) => ({
            id: c.id,
            actions: defaultActions,
            architectureId,
            childCount: c.childCount,
            description: c.description,
            parentId: c.parentId,
            selectedState: 'default' as const,
            statistics: defaultStatistics,
            status: mapPreviewStateToStatus(c.previewState),
            title: c.title,
            translations: [...c.translations],
            type: 'job-cluster' as const,
        })),
        description: node.description,
        parentId: node.parentId,
        selectedState: 'default' as const,
        statistics: defaultStatistics,
        status: mapPreviewStateToStatus(node.previewState),
        title: node.title,
        translations: [...node.translations],
        type: 'job-family' as const,
    }));

export const jsaRegenerationResultSchema = z.object({
    regenerationId: z.string(),
    preview: z.object({
        added: jsaStructureRegenerationContentSchema.default([]),
        merged: jsaStructureRegenerationContentSchema.default([]),
        removedIds: removedStructureContentSchema.default([]),
        updated: jsaStructureRegenerationContentSchema.default([]),
    }),
    createdAt: z.string(),
});

export type JsaRegenerationResult = z.infer<typeof jsaRegenerationResultSchema>;
